import type { FlowMap, FlowStep, SagaStep, StageStep } from '@cogo/types-v2'
import {
  buildActionFlowReadinessPacket as buildHeadlessActionFlowReadinessPacket,
  buildActionFlowUserOutcomeProof as buildHeadlessActionFlowUserOutcomeProof,
  deriveActionFlowUserOutcomeContract as deriveHeadlessActionFlowUserOutcomeContract,
  simulateActionFlowTopology as simulateHeadlessActionFlowTopology,
  validateActionFlowTopologyContract as validateHeadlessActionFlowTopologyContract,
} from '@cogo/headless-core/actionflow-studio-readiness'
import type {
  ActionFlowReadinessPacket,
  ActionFlowTopologySimulationProof,
  ActionFlowTopologySimulationState,
  ActionFlowUserOutcomeProof,
  ContractValidationResult,
} from '@cogo/headless-core/actionflow-studio-readiness'
import type {
  AppSpecFull,
  StudioActionFlowBinding,
  StudioActionFlowReadinessEvidence,
  StudioActionFlowRuntimeMockEvidence,
  StudioActionFlowRuntimeLifecycleEvidence,
  StudioActionFlowRuntimeSimulationIssue,
  StudioActionFlowRuntimeSimulationProof,
  StudioActionFlowRuntimeStateMutation,
  StudioActionFlowRuntimeStateScope,
} from '@/services/block-api-service'
import { withActionFlowCompilerArtifacts } from './actionflow-binding-plan'

export interface ActionFlowRuntimeFaultInjection {
  action?: string
  stepName?: string
  stageName?: string
  phase?:
    | 'onEnter'
    | 'steps'
    | 'onSuccess'
    | 'onError'
    | 'onFinally'
    | 'compensate'
  occurrence?: number
  message?: string
}

export interface BuildActionFlowRuntimeSimulationOptions {
  source?: string
  faultInjection?: ActionFlowRuntimeFaultInjection
  motionClock?: {
    initialTimeMs?: number
    defaultDurationMs?: number
  }
}

interface HeadlessActionFlowReadinessArtifacts {
  validationResult: ContractValidationResult
  simulationProof?: ActionFlowTopologySimulationProof
  userOutcomeProof?: ActionFlowUserOutcomeProof
  readinessPacket?: ActionFlowReadinessPacket
}

interface SimulationState {
  local: Record<string, Record<string, unknown>>
  appData: Record<string, unknown>
  computed: Record<string, unknown>
  _results: Record<string, unknown>
  route: Record<string, unknown>
}

interface SimulationContext {
  binding: StudioActionFlowBinding
  flowName: string
  appSpec: AppSpecFull
  state: SimulationState
  issues: StudioActionFlowRuntimeSimulationIssue[]
  stateMutations: StudioActionFlowRuntimeStateMutation[]
  mockEvidence: StudioActionFlowRuntimeMockEvidence[]
  lifecycleEvidence: StudioActionFlowRuntimeLifecycleEvidence[]
  stepsSimulated: number
  clockMs: number
  motionTimeMs: number
  stagesCompleted: number
  compensationsAttempted: number
  lifecycleOrder: number
  faultMatches: number
  currentStageName?: string
  currentPhase?: ActionFlowRuntimeFaultInjection['phase']
  options: BuildActionFlowRuntimeSimulationOptions
}

class SimulationFault extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SimulationFault'
  }
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)))
}

function hasActionFlowReadinessNeed(appSpec: AppSpecFull): boolean {
  return (
    appSpec.interactionContract?.actionFlowRequired === true ||
    appSpec.blocks.some((block) => (block.binds ?? []).length > 0) ||
    Boolean(
      appSpec.actionFlowTopology?.primitives.some(
        (primitive) => primitive.required,
      ),
    )
  )
}

function addIssue(
  issues: StudioActionFlowRuntimeSimulationIssue[],
  issue: StudioActionFlowRuntimeSimulationIssue,
): void {
  issues.push(issue)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function initialAppData(appSpec: AppSpecFull): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(appSpec.globalState ?? {}).map(([key, spec]) => [
      key,
      spec.default,
    ]),
  )
}

function sampleHeadlessNodeValue(scope: string, path: string): unknown {
  if (path === 'route.path') return '/'
  if (/permissions?|roles?|access/i.test(path)) return true
  if (scope === 'datasource') return { rows: [], simulated: true }
  if (path.endsWith('.root')) return {}
  return `sample:${path}`
}

function seedHeadlessScopePath(
  scope: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  if (!path || readPath(scope, path) !== undefined) return
  writePath(scope, path, value)
}

function buildHeadlessInitialState(
  appSpec: AppSpecFull,
): Partial<ActionFlowTopologySimulationState> {
  const state: Partial<ActionFlowTopologySimulationState> = {
    local: {},
    appData: initialAppData(appSpec),
    computed: {},
    _results: {},
    route: { path: '/', params: {}, query: {} },
    props: {},
    env: {},
    event: { value: 'sample:event.value' },
    datasource: {},
  }

  for (const node of appSpec.actionFlowTopology?.nodes ?? []) {
    const path = node.path
    if (!path.includes('.')) continue
    if (node.scope === 'local') {
      seedHeadlessScopePath(
        state.local ?? {},
        path.slice('local.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === 'appData') {
      seedHeadlessScopePath(
        state.appData ?? {},
        path.slice('appData.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === 'computed') {
      seedHeadlessScopePath(
        state.computed ?? {},
        path.slice('computed.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === '_results') {
      seedHeadlessScopePath(
        state._results ?? {},
        path.slice('_results.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === 'route') {
      seedHeadlessScopePath(
        state.route ?? {},
        path.slice('route.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === 'props') {
      seedHeadlessScopePath(
        state.props ?? {},
        path.slice('props.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === 'env') {
      seedHeadlessScopePath(
        state.env ?? {},
        path.slice('env.'.length),
        sampleHeadlessNodeValue(node.scope, path),
      )
    } else if (node.scope === 'datasource') {
      seedHeadlessScopePath(
        state.datasource ?? {},
        node.datasourceName ?? path,
        sampleHeadlessNodeValue(node.scope, path),
      )
    }
  }

  return state
}

function buildHeadlessDatasourceMocks(
  appSpec: AppSpecFull,
): Record<string, unknown> {
  return Object.fromEntries(
    (appSpec.datasources ?? []).map((datasource) => [
      datasource.name,
      {
        rows: [],
        simulated: true,
        datasourceName: datasource.name,
      },
    ]),
  )
}

function summarizeHeadlessSimulationProof(
  proof: ActionFlowTopologySimulationProof,
): NonNullable<StudioActionFlowReadinessEvidence['headlessSimulation']> {
  return {
    schemaVersion: proof.schemaVersion,
    ok: proof.ok,
    validationErrors: proof.validationErrors,
    errors: proof.errors,
    summary: { ...proof.summary },
  }
}

function summarizeUserOutcomeProof(
  proof: ActionFlowUserOutcomeProof,
): NonNullable<StudioActionFlowReadinessEvidence['userOutcomeProof']> {
  return {
    schemaVersion: proof.schemaVersion,
    contractVersion: proof.contractVersion,
    scenarioId: proof.scenarioId,
    ok: proof.ok,
    readiness: proof.readiness,
    outcomeCount: proof.outcomes.length,
    blockingFailures: proof.blockingFailures,
    evidenceRefs: proof.evidenceRefs,
  }
}

function summarizeReadinessPacket(
  packet: ActionFlowReadinessPacket,
): NonNullable<StudioActionFlowReadinessEvidence['readinessPacket']> {
  return {
    schemaVersion: packet.schemaVersion,
    status: packet.status,
    publishAllowed: packet.publishAllowed,
    stages: packet.stages,
    blockingFailures: packet.blockingFailures,
    evidenceRefs: packet.evidenceRefs,
    policy: { ...packet.policy },
  }
}

function buildHeadlessActionFlowReadinessArtifacts(
  appSpec: AppSpecFull,
  options: BuildActionFlowRuntimeSimulationOptions,
  required: boolean,
): HeadlessActionFlowReadinessArtifacts {
  const topology = appSpec.actionFlowTopology
  if (!topology) {
    return {
      validationResult: {
        ok: !required,
        errors: required ? ['ActionFlow topology contract is missing.'] : [],
      },
    }
  }

  const source = options.source ?? 'studio-actionflow-runtime-simulation'
  const validationResult = validateHeadlessActionFlowTopologyContract(topology)
  const simulationProof = simulateHeadlessActionFlowTopology(topology, {
    source: `${source}:headless-topology`,
    initialState: buildHeadlessInitialState(appSpec),
    event: { value: 'sample:event.value' },
    datasourceMocks: buildHeadlessDatasourceMocks(appSpec),
  })
  const userOutcomeContract = deriveHeadlessActionFlowUserOutcomeContract({
    scenarioId: `${appSpec.appId ?? stableAppId(appSpec.name)}:actionflow`,
    userGoal:
      appSpec.description ??
      `Generate and execute ActionFlow interactions for ${appSpec.name}.`,
    userValue:
      'User-visible interactions update state, mock side effects, and produce traceable outcomes.',
    simulationProof,
    evidenceRefs: [
      'appSpec.actionFlowTopology',
      'appSpec.actionFlowRuntimeSimulationProof',
    ],
  })
  const userOutcomeProof = buildHeadlessActionFlowUserOutcomeProof(
    userOutcomeContract,
    simulationProof,
  )
  const readinessPacket = buildHeadlessActionFlowReadinessPacket({
    validationResult,
    simulationProof,
    userOutcomeProof,
  })

  return {
    validationResult,
    simulationProof,
    userOutcomeProof,
    readinessPacket,
  }
}

function actionFlowOntologyPlanErrors(
  appSpec: AppSpecFull,
  required: boolean,
): string[] {
  if (!required) return []
  const plan = appSpec.ontologyRetrievalTrace?.actionFlowOntologyPlan
  if (!plan?.publishBlocked) return []
  return plan.gaps
    .filter((gap) => gap.severity === 'error')
    .map((gap) => {
      const missing = gap.missing.length > 0
        ? ` Missing: ${gap.missing.join(', ')}.`
        : ''
      return `ActionFlow ontology ${gap.code}: ${gap.message}${missing}`
    })
}

function stableAppId(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'generated-app'
  )
}

function readPath(scope: Record<string, unknown>, path: string): unknown {
  if (!path) return scope
  return path.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object') return undefined
    return (current as Record<string, unknown>)[segment]
  }, scope)
}

function writePath(
  scope: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const parts = path.split('.').filter(Boolean)
  if (parts.length === 0) return
  let target = scope
  for (const part of parts.slice(0, -1)) {
    const next = target[part]
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      target[part] = {}
    }
    target = target[part] as Record<string, unknown>
  }
  target[parts[parts.length - 1]!] = value
}

function sampleEventValue(binding: StudioActionFlowBinding): unknown {
  if (binding.primitiveKind === 'set-local-state')
    return `sample:${binding.event.eventType}`
  if (binding.primitiveKind === 'guard-by-permission') return true
  if (binding.primitiveKind === 'trigger-ui-feedback')
    return 'Action completed'
  return { value: `sample:${binding.primitiveKind}` }
}

function resolveExpression(value: unknown, ctx: SimulationContext): unknown {
  if (typeof value !== 'string') return value
  const match = value.match(/^#\{(.+)\}$/)
  if (!match) return value
  const expr = match[1]!.trim()
  if (expr === 'event.value') return sampleEventValue(ctx.binding)
  if (expr.startsWith('local.')) {
    return (
      readPath(
        ctx.state.local[ctx.binding.event.blockName] ?? {},
        expr.slice('local.'.length),
      ) ?? `sample:${expr}`
    )
  }
  if (expr.startsWith('appData.'))
    return (
      readPath(ctx.state.appData, expr.slice('appData.'.length)) ??
      `sample:${expr}`
    )
  if (expr === 'appData') return ctx.state.appData
  if (expr.startsWith('computed.'))
    return (
      readPath(ctx.state.computed, expr.slice('computed.'.length)) ??
      `sample:${expr}`
    )
  if (expr.startsWith('_results.'))
    return (
      readPath(ctx.state._results, expr.slice('_results.'.length)) ??
      `sample:${expr}`
    )
  if (expr.startsWith('route.'))
    return (
      readPath(ctx.state.route, expr.slice('route.'.length)) ?? `sample:${expr}`
    )
  return `sample:${expr}`
}

function parseTarget(
  target: string,
  binding: StudioActionFlowBinding,
): {
  scope: StudioActionFlowRuntimeStateScope
  blockName?: string
  path: string
} | null {
  if (!target.trim()) return null
  if (target.startsWith('local.')) {
    return {
      scope: 'local',
      blockName: binding.event.blockName,
      path: target.slice('local.'.length),
    }
  }
  if (target.startsWith('appData.'))
    return { scope: 'appData', path: target.slice('appData.'.length) }
  if (target === 'appData') return { scope: 'appData', path: 'appData' }
  if (target.startsWith('computed.'))
    return { scope: 'computed', path: target.slice('computed.'.length) }
  if (target.startsWith('_results.'))
    return { scope: '_results', path: target.slice('_results.'.length) }
  if (target.startsWith('route.'))
    return { scope: 'route', path: target.slice('route.'.length) }
  if (/^[a-zA-Z0-9_.-]+$/.test(target)) {
    return { scope: 'local', blockName: binding.event.blockName, path: target }
  }
  return null
}

function writeTarget(
  target: string | undefined,
  value: unknown,
  sourceAction: string,
  ctx: SimulationContext,
): void {
  if (!target) {
    addIssue(ctx.issues, {
      code: 'set-state-target-missing',
      severity: 'error',
      bindingId: ctx.binding.id,
      flowName: ctx.flowName,
      message: `Action "${sourceAction}" is missing a state target.`,
    })
    return
  }

  const parsed = parseTarget(target, ctx.binding)
  if (!parsed || parsed.scope === 'route') {
    addIssue(ctx.issues, {
      code: 'state-write-scope-invalid',
      severity: 'error',
      bindingId: ctx.binding.id,
      flowName: ctx.flowName,
      message: `Action "${sourceAction}" writes unsupported target "${target}".`,
    })
    return
  }

  if (parsed.scope === 'local') {
    const blockName = parsed.blockName ?? ctx.binding.event.blockName
    ctx.state.local[blockName] ??= {}
    writePath(ctx.state.local[blockName]!, parsed.path, value)
  } else if (parsed.scope === 'appData') {
    writePath(ctx.state.appData, parsed.path, value)
  } else if (parsed.scope === 'computed') {
    writePath(ctx.state.computed, parsed.path, value)
  } else if (parsed.scope === '_results') {
    writePath(ctx.state._results, parsed.path, value)
  }

  ctx.stateMutations.push({
    bindingId: ctx.binding.id,
    flowName: ctx.flowName,
    scope: parsed.scope,
    path:
      parsed.scope === 'local'
        ? `${parsed.blockName}.${parsed.path}`
        : parsed.path,
    sourceAction,
    evidenceRefs: ctx.binding.evidenceRefs,
  })
}

function executeBasicStep(step: FlowStep, ctx: SimulationContext): void {
  const record = step as unknown as Record<string, unknown>
  const action = typeof record.action === 'string' ? record.action : 'unknown'
  const params = asRecord(record.params)
  const stepName = typeof record.name === 'string' ? record.name : undefined

  const fault = ctx.options.faultInjection
  if (fault) {
    const matches =
      (!fault.action || fault.action === action) &&
      (!fault.stepName || fault.stepName === stepName) &&
      (!fault.stageName || fault.stageName === ctx.currentStageName) &&
      (!fault.phase || fault.phase === ctx.currentPhase)
    if (matches) {
      ctx.faultMatches += 1
      if (ctx.faultMatches === (fault.occurrence ?? 1)) {
        throw new SimulationFault(
          fault.message ??
            `Injected failure for ${stepName ?? action} in ${ctx.currentStageName ?? ctx.flowName}.`,
        )
      }
    }
  }

  if (action === 'setState') {
    writeTarget(
      typeof params.target === 'string' ? params.target : undefined,
      resolveExpression(params.value, ctx),
      action,
      ctx,
    )
    if (stepName) {
      writeTarget(`_results.${stepName}`, { ok: true, action }, action, ctx)
    }
    return
  }

  if (action === 'restApi') {
    const datasourceName =
      typeof params.datasourceName === 'string'
        ? params.datasourceName
        : 'mock-datasource'
    const method =
      typeof params.method === 'string' ? params.method.toUpperCase() : 'GET'
    const resultTarget =
      typeof params.resultTarget === 'string'
        ? params.resultTarget
        : `_results.${stepName ?? 'restApiResult'}`
    const resultValue = {
      data: {
        datasourceName,
        rows: [],
        simulated: true,
        ...(method === 'SUBSCRIBE' ? { realtime: true } : {}),
      },
    }
    writeTarget(resultTarget, resultValue, action, ctx)
    ctx.mockEvidence.push({
      bindingId: ctx.binding.id,
      flowName: ctx.flowName,
      kind: method === 'SUBSCRIBE' ? 'realtime' : 'datasource',
      target: datasourceName,
      resultRef: resultTarget,
      evidenceRefs: ctx.binding.evidenceRefs,
    })
    return
  }

  if (action === 'showToast') {
    const message =
      typeof params.message === 'string'
        ? params.message
        : String(resolveExpression(params.message, ctx) ?? 'Action completed')
    ctx.mockEvidence.push({
      bindingId: ctx.binding.id,
      flowName: ctx.flowName,
      kind: 'ui',
      target: message,
      evidenceRefs: ctx.binding.evidenceRefs,
    })
    if (stepName)
      writeTarget(
        `_results.${stepName}`,
        { ok: true, action, message },
        action,
        ctx,
      )
    return
  }

  if (action === 'navigate') {
    const target = resolveExpression(params.to, ctx)
    ctx.mockEvidence.push({
      bindingId: ctx.binding.id,
      flowName: ctx.flowName,
      kind: 'navigation',
      target: String(target ?? '/'),
      evidenceRefs: ctx.binding.evidenceRefs,
    })
    if (stepName)
      writeTarget(`_results.${stepName}`, { ok: true, to: target }, action, ctx)
    return
  }

  if (action === 'runMotion') {
    const configuredDuration =
      typeof params.durationMs === 'number' &&
      Number.isFinite(params.durationMs)
        ? Math.max(0, params.durationMs)
        : undefined
    const durationMs =
      configuredDuration ?? ctx.options.motionClock?.defaultDurationMs ?? 300
    const startedAtMs = ctx.clockMs
    ctx.clockMs += durationMs
    ctx.motionTimeMs += durationMs
    const target =
      typeof params.target === 'string' ? params.target : 'unknown-element'
    ctx.mockEvidence.push({
      bindingId: ctx.binding.id,
      flowName: ctx.flowName,
      kind: 'motion',
      target,
      startedAtMs,
      completedAtMs: ctx.clockMs,
      durationMs,
      evidenceRefs: ctx.binding.evidenceRefs,
    })
    if (stepName) {
      writeTarget(
        `_results.${stepName}`,
        { ok: true, action, target, completedAtMs: ctx.clockMs },
        action,
        ctx,
      )
    }
    return
  }

  addIssue(ctx.issues, {
    code: 'flow-step-unsupported',
    severity: 'error',
    bindingId: ctx.binding.id,
    flowName: ctx.flowName,
    stepName,
    message: `Unsupported basic ActionFlow action "${action}".`,
  })
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function recordLifecycle(
  ctx: SimulationContext,
  kind: 'stage' | 'saga',
  phase: StudioActionFlowRuntimeLifecycleEvidence['phase'],
  stageName?: string,
  error?: unknown,
): void {
  ctx.lifecycleOrder += 1
  ctx.lifecycleEvidence.push({
    bindingId: ctx.binding.id,
    flowName: ctx.flowName,
    kind,
    phase,
    ...(stageName ? { stageName } : {}),
    order: ctx.lifecycleOrder,
    clockMs: ctx.clockMs,
    ...(error === undefined ? {} : { error: errorMessage(error) }),
    evidenceRefs: ctx.binding.evidenceRefs,
  })
}

function executeStageBranch(
  branch: FlowStep[] | undefined,
  phase: NonNullable<SimulationContext['currentPhase']>,
  stageName: string,
  ctx: SimulationContext,
): void {
  if (!branch) return
  const previousStageName = ctx.currentStageName
  const previousPhase = ctx.currentPhase
  ctx.currentStageName = stageName
  ctx.currentPhase = phase
  recordLifecycle(ctx, 'stage', phase, stageName)
  try {
    for (const child of branch) executeStep(child, ctx)
  } finally {
    ctx.currentStageName = previousStageName
    ctx.currentPhase = previousPhase
  }
}

function executeStage(step: StageStep, ctx: SimulationContext): boolean {
  let pendingError: unknown
  let hasPendingError = false
  let hookFailed = false

  try {
    executeStageBranch(step.onEnter, 'onEnter', step.name, ctx)
    executeStageBranch(step.steps, 'steps', step.name, ctx)
    executeStageBranch(step.onSuccess, 'onSuccess', step.name, ctx)
  } catch (error) {
    pendingError = error
    hasPendingError = true
    if (step.onError?.length) {
      try {
        executeStageBranch(step.onError, 'onError', step.name, ctx)
      } catch (onErrorFailure) {
        pendingError = onErrorFailure
        hookFailed = true
      }
    }
  } finally {
    if (step.onFinally?.length) {
      try {
        executeStageBranch(step.onFinally, 'onFinally', step.name, ctx)
      } catch (onFinallyFailure) {
        pendingError = onFinallyFailure
        hasPendingError = true
        hookFailed = true
      }
    }
  }

  if (hasPendingError) {
    if (
      (step.failurePolicy ?? 'stop') === 'continue' &&
      Boolean(step.onError?.length) &&
      !hookFailed
    ) {
      recordLifecycle(ctx, 'stage', 'handled', step.name, pendingError)
      return false
    }
    recordLifecycle(ctx, 'stage', 'failed', step.name, pendingError)
    throw pendingError
  }

  ctx.stagesCompleted += 1
  recordLifecycle(ctx, 'stage', 'completed', step.name)
  return true
}

function executeSaga(step: SagaStep, ctx: SimulationContext): void {
  const completed: StageStep[] = []
  try {
    for (const stage of step.stages) {
      if (executeStage(stage, ctx) && stage.compensate?.length)
        completed.push(stage)
    }
    recordLifecycle(ctx, 'saga', 'completed')
  } catch (forwardFailure) {
    for (const stage of [...completed].reverse()) {
      ctx.compensationsAttempted += 1
      recordLifecycle(ctx, 'saga', 'compensate', stage.name, forwardFailure)
      try {
        executeStageBranch(stage.compensate, 'compensate', stage.name, ctx)
      } catch (_compensationFailure) {
        if ((step.compensationFailurePolicy ?? 'continue') === 'stop') break
      }
    }
    recordLifecycle(ctx, 'saga', 'failed', undefined, forwardFailure)
    throw forwardFailure
  }
}

function executeStep(step: FlowStep, ctx: SimulationContext): void {
  ctx.stepsSimulated += 1
  const record = step as unknown as Record<string, unknown>
  if (record.type === 'basic') {
    executeBasicStep(step, ctx)
    return
  }
  if (record.type === 'expression') {
    const params = asRecord(record.params)
    writeTarget(
      typeof params.target === 'string' ? params.target : undefined,
      resolveExpression(params.expression, ctx),
      'compute',
      ctx,
    )
    return
  }
  if (record.type === 'conditional') {
    const condition = resolveExpression(record.condition, ctx)
    const branch = condition ? record.then : record.else
    if (Array.isArray(branch)) {
      for (const branchStep of branch) executeStep(branchStep as FlowStep, ctx)
    }
    return
  }
  if (record.type === 'stage') {
    executeStage(step as StageStep, ctx)
    return
  }
  if (record.type === 'saga') {
    executeSaga(step as SagaStep, ctx)
    return
  }

  addIssue(ctx.issues, {
    code: 'flow-step-unsupported',
    severity: 'error',
    bindingId: ctx.binding.id,
    flowName: ctx.flowName,
    stepName: typeof record.name === 'string' ? record.name : undefined,
    message: `Unsupported ActionFlow step type "${String(record.type)}".`,
  })
}

export function simulateActionFlowRuntime(
  appSpec: AppSpecFull,
  options: BuildActionFlowRuntimeSimulationOptions = {},
): StudioActionFlowRuntimeSimulationProof {
  const source = options.source ?? 'studio-actionflow-runtime-simulation'
  const issues: StudioActionFlowRuntimeSimulationIssue[] = []
  const stateMutations: StudioActionFlowRuntimeStateMutation[] = []
  const mockEvidence: StudioActionFlowRuntimeMockEvidence[] = []
  const lifecycleEvidence: StudioActionFlowRuntimeLifecycleEvidence[] = []
  const state: SimulationState = {
    local: {},
    appData: initialAppData(appSpec),
    computed: {},
    _results: {},
    route: { path: '/', params: {}, query: {} },
  }

  const bindingPlan = appSpec.actionFlowBindingPlan
  const flows: FlowMap | undefined = appSpec.actionFlowCompiledFlows
  if (!bindingPlan || !flows) {
    return {
      schemaVersion: 'studio-actionflow-runtime-simulation-proof.v1',
      source,
      generatedAt: new Date().toISOString(),
      valid: false,
      issues: [
        {
          code: 'missing-compiled-flows',
          severity: 'error',
          message:
            'Runtime simulation requires a binding plan and compiled flow map.',
        },
      ],
      stateMutations: [],
      mockEvidence: [],
      summary: {
        bindingsSimulated: 0,
        flowsSimulated: 0,
        stepsSimulated: 0,
        stateWrites: 0,
        resultWrites: 0,
        datasourceMocks: 0,
        sideEffectMocks: 0,
      },
    }
  }

  let flowsSimulated = 0
  let stepsSimulated = 0
  for (const binding of bindingPlan.bindings) {
    const flow = flows[binding.flowName]
    if (!flow) {
      addIssue(issues, {
        code: 'binding-flow-missing',
        severity: 'error',
        bindingId: binding.id,
        flowName: binding.flowName,
        message: `Binding "${binding.id}" has no compiled flow "${binding.flowName}".`,
      })
      continue
    }
    flowsSimulated += 1
    const ctx: SimulationContext = {
      binding,
      flowName: binding.flowName,
      appSpec,
      state,
      issues,
      stateMutations,
      mockEvidence,
      lifecycleEvidence,
      stepsSimulated: 0,
      clockMs: options.motionClock?.initialTimeMs ?? 0,
      motionTimeMs: 0,
      stagesCompleted: 0,
      compensationsAttempted: 0,
      lifecycleOrder: 0,
      faultMatches: 0,
      options,
    }
    try {
      for (const step of flow.steps ?? []) executeStep(step as FlowStep, ctx)
    } catch (error) {
      addIssue(issues, {
        code: 'flow-execution-failed',
        severity: 'error',
        bindingId: binding.id,
        flowName: binding.flowName,
        message: `ActionFlow "${binding.flowName}" failed: ${errorMessage(error)}`,
      })
    }
    stepsSimulated += ctx.stepsSimulated
  }

  if (appSpec.interactionContract?.requireResultsTrace) {
    const hasResultWrite = stateMutations.some(
      (mutation) => mutation.scope === '_results',
    )
    if (!hasResultWrite) {
      addIssue(issues, {
        code: 'runtime-result-missing',
        severity: 'error',
        message:
          'Runtime simulation requires at least one _results write for traceable ActionFlow execution.',
      })
    }
  }

  return {
    schemaVersion: 'studio-actionflow-runtime-simulation-proof.v1',
    source,
    generatedAt: new Date().toISOString(),
    valid: !issues.some((issue) => issue.severity === 'error'),
    issues,
    stateMutations,
    mockEvidence,
    lifecycleEvidence,
    summary: {
      bindingsSimulated: bindingPlan.bindings.length,
      flowsSimulated,
      stepsSimulated,
      stateWrites: stateMutations.filter(
        (mutation) => mutation.scope !== '_results',
      ).length,
      resultWrites: stateMutations.filter(
        (mutation) => mutation.scope === '_results',
      ).length,
      datasourceMocks: mockEvidence.filter((mock) => mock.kind === 'datasource')
        .length,
      sideEffectMocks: mockEvidence.length,
      stagesCompleted: lifecycleEvidence.filter(
        (item) => item.kind === 'stage' && item.phase === 'completed',
      ).length,
      compensationsAttempted: lifecycleEvidence.filter(
        (item) => item.kind === 'saga' && item.phase === 'compensate',
      ).length,
      motionTimeMs: mockEvidence
        .filter((item) => item.kind === 'motion')
        .reduce((total, item) => total + (item.durationMs ?? 0), 0),
    },
  }
}

export function validateActionFlowRuntimeSimulationProof(
  proof: StudioActionFlowRuntimeSimulationProof | null | undefined,
): StudioActionFlowRuntimeSimulationProof {
  if (!proof) {
    return {
      schemaVersion: 'studio-actionflow-runtime-simulation-proof.v1',
      source: 'studio-actionflow-runtime-simulation',
      generatedAt: new Date().toISOString(),
      valid: false,
      issues: [
        {
          code: 'missing-compiled-flows',
          severity: 'error',
          message: 'ActionFlow runtime simulation proof is missing.',
        },
      ],
      stateMutations: [],
      mockEvidence: [],
      summary: {
        bindingsSimulated: 0,
        flowsSimulated: 0,
        stepsSimulated: 0,
        stateWrites: 0,
        resultWrites: 0,
        datasourceMocks: 0,
        sideEffectMocks: 0,
      },
    }
  }
  return proof
}

export function buildActionFlowReadinessEvidence(
  appSpec: AppSpecFull,
  runtimeProof: StudioActionFlowRuntimeSimulationProof | null | undefined,
  options: BuildActionFlowRuntimeSimulationOptions = {},
): StudioActionFlowReadinessEvidence {
  const required = hasActionFlowReadinessNeed(appSpec)
  const topologyProof = appSpec.actionFlowValidationProof
  const compilerProof = appSpec.actionFlowCompilerProof
  const topologyValid = topologyProof?.valid === true
  const compilerValid = compilerProof?.valid === true
  const validatedRuntimeProof =
    validateActionFlowRuntimeSimulationProof(runtimeProof)
  const runtimeValid = validatedRuntimeProof.valid
  const headless = buildHeadlessActionFlowReadinessArtifacts(
    appSpec,
    options,
    required,
  )
  const headlessValidationValid = headless.validationResult.ok
  const headlessSimulationValid = headless.simulationProof?.ok ?? !required
  const userOutcomeValid = headless.userOutcomeProof?.ok ?? !required
  const ontologyErrors = topologyProof?.issues
    .filter((issue) => issue.severity === 'error')
    .map((issue) => issue.message) ?? [
    'ActionFlow ontology validation proof is missing.',
  ]
  const compilerErrors = compilerProof?.issues
    .filter((issue) => issue.severity === 'error')
    .map((issue) => issue.message) ?? ['ActionFlow compiler proof is missing.']
  const runtimeErrors = validatedRuntimeProof.issues
    .filter((issue) => issue.severity === 'error')
    .map((issue) => issue.message)
  const ontologyPlanErrors = actionFlowOntologyPlanErrors(appSpec, required)
  const headlessErrors = [
    ...(headlessValidationValid ? [] : headless.validationResult.errors),
    ...(headlessSimulationValid
      ? []
      : [
          ...(headless.simulationProof?.validationErrors ?? []),
          ...(headless.simulationProof?.errors ?? []),
        ]),
    ...(userOutcomeValid
      ? []
      : (headless.userOutcomeProof?.blockingFailures ?? [])),
  ]

  const reasons = !required && !appSpec.actionFlowTopology ? [] : [
    ...(topologyValid ? [] : ontologyErrors),
    ...(compilerValid ? [] : compilerErrors),
    ...(runtimeValid ? [] : runtimeErrors),
    ...(ontologyPlanErrors.length > 0 ? ontologyPlanErrors : []),
    ...(headlessErrors.length > 0 ? headlessErrors : []),
  ]
  const primitiveCount = appSpec.actionFlowTopology?.primitives.length ?? 0
  const compilerParamBindingCount =
    appSpec.actionFlowTopology?.primitives.reduce(
      (count, primitive) =>
        count + (primitive.compilerParamBindings?.length ?? 0),
      0,
    ) ?? 0
  const contractReady =
    topologyValid &&
    compilerValid &&
    runtimeValid &&
    ontologyPlanErrors.length === 0 &&
    headlessValidationValid &&
    headlessSimulationValid &&
    userOutcomeValid
  const ready = required
    ? contractReady
    : !appSpec.actionFlowTopology || contractReady
  const status: StudioActionFlowReadinessEvidence['status'] =
    !required && ready
      ? 'not-required'
      : contractReady
        ? 'ready'
        : appSpec.actionFlowTopology
          ? 'invalid'
          : 'gap'

  return {
    schemaVersion: 'studio-actionflow-readiness.v1',
    contractVersion: 'studio-actionflow-state-topology.v1',
    status,
    required,
    generatedAt: new Date().toISOString(),
    evidenceRefs: uniqueStrings([
      'appSpec.actionFlowTopology',
      'appSpec.actionFlowValidationProof',
      '@cogo/headless-core.validateActionFlowTopologyContract',
      'appSpec.actionFlowBindingPlan',
      'appSpec.actionFlowCompilerProof',
      'appSpec.actionFlowRuntimeSimulationProof',
      '@cogo/headless-core.simulateActionFlowTopology',
      '@cogo/headless-core.deriveActionFlowUserOutcomeContract',
      '@cogo/headless-core.buildActionFlowUserOutcomeProof',
      '@cogo/headless-core.buildActionFlowReadinessPacket',
      ...(appSpec.ontologyRetrievalTrace?.actionFlowOntologyPlan
        ? [
            'appSpec.ontologyRetrievalTrace.actionFlowOntologyPlan',
            '@cogo/headless-core.planActionFlowOntology',
          ]
        : []),
    ]),
    reasons,
    ontologyValidation: {
      ok: topologyValid,
      errors: topologyValid ? [] : ontologyErrors,
    },
    headlessValidation: {
      ok: headless.validationResult.ok,
      errors: headless.validationResult.errors,
    },
    ...(headless.simulationProof
      ? {
          headlessSimulation: summarizeHeadlessSimulationProof(
            headless.simulationProof,
          ),
        }
      : {}),
    ...(headless.userOutcomeProof
      ? { userOutcomeProof: summarizeUserOutcomeProof(headless.userOutcomeProof) }
      : {}),
    ...(headless.readinessPacket
      ? { readinessPacket: summarizeReadinessPacket(headless.readinessPacket) }
      : {}),
    summary: {
      topologyValid,
      compilerValid,
      runtimeValid,
      headlessValidationValid,
      headlessSimulationValid,
      userOutcomeValid,
      headlessPublishAllowed: headless.readinessPacket?.publishAllowed,
      primitiveCount,
      compilerParamBindingCount,
      runtimeFlowsSimulated: validatedRuntimeProof.summary.flowsSimulated,
      ...(appSpec.ontologyRetrievalTrace?.actionFlowOntologyPlan
        ? {
            actionFlowOntologyPlanStatus:
              appSpec.ontologyRetrievalTrace.actionFlowOntologyPlan.status,
            actionFlowOntologyGapCount:
              appSpec.ontologyRetrievalTrace.actionFlowOntologyPlan.gaps.length,
          }
        : {}),
    },
  }
}

export function withActionFlowRuntimeSimulationProof(
  appSpec: AppSpecFull,
  options: BuildActionFlowRuntimeSimulationOptions = {},
): AppSpecFull {
  const withCompiler = withActionFlowCompilerArtifacts(appSpec, options)
  const runtimeProof =
    withCompiler.actionFlowRuntimeSimulationProof ??
    simulateActionFlowRuntime(withCompiler, options)
  const withRuntime = {
    ...withCompiler,
    actionFlowRuntimeSimulationProof: runtimeProof,
  }
  return {
    ...withRuntime,
    actionFlowReadiness:
      withRuntime.actionFlowReadiness ??
      buildActionFlowReadinessEvidence(withRuntime, runtimeProof, options),
  }
}
